import { useState, useEffect, Fragment } from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { useDisclosure } from "@heroui/modal";
import { Button } from "@heroui/button";
import { Image } from "@heroui/image";
import { Link } from "@heroui/link";
import { Tooltip } from "@heroui/tooltip";
import { Avatar, AvatarGroup } from "@heroui/avatar";
import { MapPin, ExternalLink, AlertCircle, Pen, AudioLines, Download, Mail, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {  Drawer,  DrawerContent,  DrawerHeader,  DrawerBody,  DrawerFooter} from "@heroui/drawer";
import {Tabs, Tab} from "@heroui/tabs";
import { getByShortCode as getByShortCodeApi } from "@/api/meets";
import { getSummaryByMeetsId as getSummaryByMeetsIdApi } from "@/api/meets-summary";
import { getDetailedByMeetsId as getDetailedByMeetsIdApi } from "@/api/meets-detailed";
import { formatDate, isMeetingActive, isMeetingPast, isMeetingUpcoming } from "@/utils/dates";
import TabsComp from "../tabs-comp";
import ViewMeetsCountdown from "../countdown/view-meets-countdown";
import PulledDownWrapper from "../wrappers/pulled-down-wrapper";
import MeetingAudioPlayer from "../audio-player";
import LocationMapsChooser from "../maps/LocationMapsChooser";
import { getAudioSecureUrl as getAudioSecureUrlAPi, 
getAudioDownloadUrl as getAudioDownloadUrlApi } from "@/api/audio";
import MeetsMembersComp from "../members/meets-members-comp";

const ViewMeetsComp = ({_shortCode}) => {
const { session } = useSession();
const { user } = useUser()
const navigate = useNavigate()
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
const pre_style = {whiteSpace: "pre-wrap",  wordWrap: "break-word",  fontFamily: "inherit", fontSize: "1rem"};
const [record, setRecord] = useState(null);
const [summaries, setSummaries] = useState([]);
const [detaileds, setDetaileds] = useState([]);
const [selectedTab, setSelectedTab] = useState('summary');
const [isMeetsFinished, setIsMeetsFinished] = useState(false)
const [isMeetsStillActive, setIsMeetsStillActive] = useState(false)
const [isMeetsStillUpcoming, setIsMeetsStillUpcoming] = useState(true)
const [audioUrl, setAudioUrl] = useState(null)

const {isOpen, onOpen, onOpenChange} = useDisclosure();

const tabs = [
  { key: "summary", title: "Summary" },
  { key: "detailed", title: "Detailed" },
  { key: "participants", title: "Participants" }
]

useEffect(() => {
  getByShortCode()
}, [])

async function getByShortCode() {
  const token = await session.getToken();
  getByShortCodeApi(_shortCode, token).then((_resp) => {
    if(_resp.data.Result === 'OK'){
      const record = _resp.data.Record
      setRecord(record)
      const meets_date = record['meeting_date'];
      const start_time = record['start_time']['set'];
      const end_time = record['end_time']['real'];
      const is_past = isMeetingPast(meets_date, end_time);
      setIsMeetsFinished(is_past);

      if(is_past){
        getMeetsSummaries(record['_id'])
        getMeetsDetailed(record['_id'])
        setIsMeetsStillUpcoming(false)
      }else{
        const is_upcoming = isMeetingUpcoming(meets_date, start_time)
        setIsMeetsStillUpcoming(is_upcoming)
      }
    }
  }).catch((_resp) => {
    console.log(_resp)
    setRecord(null)
  }) 
}

async function getMeetsSummaries(_meets_id) {
  const token = await session.getToken();
  getSummaryByMeetsIdApi(_meets_id, token).then((_resp) => {
    if(_resp.data.Result === 'OK'){
      setSummaries(_resp.data.Records)
    }
  }).catch((_resp) => {
    console.log(_resp)
    setSummaries([])
  })
}

async function getMeetsDetailed(_meets_id) {
  const token = await session.getToken();
  getDetailedByMeetsIdApi(_meets_id, token).then((_resp) => {
    if(_resp.data.Result === 'OK'){
      setDetaileds(_resp.data.Records)
    }
  }).catch((_resp) => {
    console.log(_resp)
    setDetaileds([])
  })
}

function onTabChange(_key){
  setSelectedTab(_key)
}

async function onAudioDownloadClick() {
  const token = await session.getToken();
  const meetingId = record['_id'];

  getAudioDownloadUrlApi(meetingId, token).then(async (_resp) => {
      if (_resp.data.Result === 'OK') {
        const download_link = _resp.data.url;

        try {
          const res = await fetch(download_link, { cache: "no-store" });
          if (!res.ok) {
            throw new Error(`Failed to fetch file: ${res.status}`);
          }

          const blob = await res.blob();

          let filename = `${meetingId}-merged.wav`;

          const cd = res.headers.get("Content-Disposition");
          if (cd) {
            const plain = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
            if (plain && plain[1]) {
              filename = decodeURIComponent(plain[1]);
            }
          }

          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err) {
          console.error("Download error:", err);
        }
      }
    }).catch((_resp) => {
      console.error(_resp);
    });
}

async function onPlayClick() {
  const token = await session.getToken();
  const meetingId = record['_id'];
  getAudioDownloadUrlApi(meetingId, token).then((_resp) => {
    if (_resp.data.Result === 'OK') {
        const download_link = _resp.data.url;
        window.open(download_link, "_blank", "noopener,noreferrer");
    }
  })
}

if(record === null){
  return <><h2>Loading...</h2></>
}

return (<>
<PulledDownWrapper onRefresh={getByShortCode}>
  <div className="flex flex-col gap-2">
    <h1 className="text-2xl font-bold leading-7 capitalize pt-2">{record['title']}</h1>
    <p className="text-sm text-neutral-600 dark:text-zinc-300 first-letter:uppercase">
      {record["description"] === "" ? (
        <span className="italic text-neutral-400 dark:text-default-500">
          No Description...
        </span>
      ) : (
        <span>{record["description"]}</span>
      )}
    </p>
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex gap-3 items-center">
        <div className="flex-none border border-neutral-200 dark:border-white/10 rounded-md text-center w-11 overflow-hidden bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
          <div className="text-tiny bg-neutral-100 dark:bg-white/10 py-0.5 text-neutral-600 dark:text-white/70">
            {new Date(record["meeting_date"]).toLocaleString(undefined, { month: "short" })}
          </div>
          <div className="flex items-center justify-center font-semibold text-neutral-800 dark:text-white/80 text-sm h-6">
            {record["meeting_date"].substring(8, 10)}
          </div>
        </div>
        <div className="flex flex-col gap-0.5">
          <p className="text-medium text-foreground font-medium">
            {formatDate(record['meeting_date'])}
          </p>
          <p className="text-small text-default-500">{record['start_time']['set']} - {record['end_time']['set']}</p>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <LocationMapsChooser record={record}/>

        <div className="flex gap-3 items-center">
          <div className="flex items-center justify-center  border border-neutral-200 dark:border-white/10 rounded-md w-11 h-11 bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
          <Avatar name="HeroUI" size="sm" className="w-6 h-6" radius="full" src={`https://api.dicebear.com/9.x/thumbs/svg?seed=${record['created_by']['user_email']}`}/>
        </div>

          <div className="flex flex-col gap-0.5">
            <span className=" text-medium text-foreground font-medium">Hosted By</span>
            <span className="text-small text-default-500">
              {record['created_by']['user_email']}
            </span>
          </div>
        </div>
        {isMeetsFinished?
          <div onClick={() => onOpen()} className="flex gap-3 items-center">
            <div className="flex items-center justify-center  border border-neutral-200 dark:border-white/10 rounded-md w-11 h-11 bg-white dark:bg-white/5 shadow-sm dark:shadow-none">
            <AudioLines/>
          </div>
            <div className="flex flex-col gap-0.5">
              <span className=" text-medium text-foreground font-medium">Audio</span>
              <span className="text-small text-default-500">
                Click to download
              </span>
            </div>
          </div>
        : null}
      </div>
      {!isMeetsStillUpcoming?
        <div className={`flex flex-col mt-4 gap-3 items-start mb-20 ${summaries.length > 0 ? 'border-medium border-default-200 rounded-xl' : ''}`}>
          {(() => {
            const summaryEmpty = !isMeetsFinished || summaries.length === 0;
            return (
              <div className={`${selectedTab === "summary" ? "block" : "hidden"} w-full min-h-[300px] p-2 ${summaryEmpty ? "flex items-center justify-center" : ""}`}>
                {isMeetsFinished ? (
                  summaries.length > 0 ? (
                    <pre
                      style={pre_style}
                      className="max-w-prose whitespace-pre-wrap text-neutral-800 dark:text-zinc-100 bg-white dark:bg-transparent rounded-lg dark:border-none shadow-sm dark:shadow-none"
                    >
                      {summaries.map((rec) => (
                        <Fragment key={rec["_id"]}>{rec["text"]}</Fragment>
                      ))}
                    </pre>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <AlertCircle className="w-8 h-8 text-default-600" />
                      <h2 className="text-lg font-semibold text-default-500">Summary Not Available</h2>
                      <p className="text-sm text-default-500">Still Processing.</p>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <AlertCircle className="w-8 h-8 text-default-600" />
                    <h2 className="text-lg font-semibold text-default-500">Summary Not Available</h2>
                    <p className="text-sm text-default-500">The meeting is still active.</p>
                  </div>
                )}
              </div>
            );
          })()}
          {(() => {
            const detailedEmpty = !isMeetsFinished || detaileds.length === 0;
            return (
              <div className={`${selectedTab === "detailed" ? "block" : "hidden"} w-full min-h-[300px] p-2 ${detailedEmpty ? "flex items-center justify-center" : ""}`}>
                {isMeetsFinished ? (
                  detaileds.length > 0 ? (
                    <pre
                      style={pre_style}
                      className="max-w-prose whitespace-pre-wrap text-neutral-800 dark:text-zinc-100 bg-white dark:bg-transparent dark:border-none shadow-sm dark:shadow-none"
                    >
                      {detaileds.map((rec) => (
                        <Fragment key={rec["_id"]}>{rec["text"]}</Fragment>
                      ))}
                    </pre>
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-center">
                      <AlertCircle className="w-8 h-8 text-default-600" />
                      <h2 className="text-lg font-semibold text-default-500">Detailed Not Available</h2>
                      <p className="text-sm text-default-500">Still Processing.</p>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center gap-2 text-center">
                    <AlertCircle className="w-8 h-8 text-default-600" />
                    <h2 className="text-lg font-semibold text-default-500">Detailed Not Available</h2>
                    <p className="text-sm text-default-500">The meeting is still active.</p>
                  </div>
                )}
              </div>
            );
          })()}
          <div className={`${selectedTab === "participants" ? "flex" : "hidden"} w-full items-center justify-center`}>
            {/* <h3 className="text-default-500">Under Development</h3> */}
            <MeetsMembersComp _members={record['members']}/>
          </div>
        </div>
      : 
        <div className="flex flex-col mt-4 gap-3 items-start mb-20 p-2">
          <div className="flex min-h-[300px] w-full items-center justify-center">
            <ViewMeetsCountdown meetsRecord={record}/>
          </div>
        </div>
      }
    </div>
  </div>
</PulledDownWrapper>
{!isMeetsStillUpcoming?
    <div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[50]">
      <TabsComp items={tabs} selectedKey={selectedTab} defaultKey="today" onChange={onTabChange}/>
    </div>
  : 
    <Fragment>
      {user['id'] === record['created_by']['user_id']?
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[100px] z-[50] w-full ps-4 pe-4 pointer-events-none">
          <div className="flex justify-between ps-4 pe-4 pointer-events-auto">
            <div>
            
            </div>
            <div>
              <Button onPress={() => navigate(`/meetings/edit/${_shortCode}`)} isIconOnly size="md" radius="full" color="primary" variant="solid" className="flex items-center gap-2 flex-1 float-end" >
                <Pen size={16}/>
              </Button>
            </div>
          </div>
        </div>
      : null}
    </Fragment>
  }

<Drawer isOpen={isOpen} onOpenChange={onOpenChange} classNames={{ backdrop: "bg-black/30" }} backdrop={"blur"} placement="bottom">
  <DrawerContent>
  {(onClose) => (
    <>
      <DrawerHeader className="flex flex-col gap-1">Get Audio</DrawerHeader>
      <DrawerBody>
        <p className="text-default-600">
          Choose how you’d like to receive your audio file:
        </p>
      </DrawerBody>

      <DrawerFooter className="flex flex-col sm:flex-row gap-3">
        <Button fullWidth color="primary" startContent={<Download className="h-4 w-4" />}
          onPress={() => { onClose(); onAudioDownloadClick()}}> 
          Download
        </Button>

        <Button fullWidth variant="flat" startContent={<Mail className="h-4 w-4" />}
          onPress={() => {onClose();}}>
          Email
        </Button>

        <Button fullWidth color="success" startContent={<Play className="h-4 w-4" />}
          onPress={() => { onClose(); onPlayClick()}}>
          Play
        </Button>
      </DrawerFooter>
    </>
  )}
</DrawerContent>
</Drawer>
</>)
}
export default ViewMeetsComp